import React, { useEffect, useState, useCallback } from "react";
import { Table, Pagination, Input, Select, Dropdown, Button, Modal, message, Tag, DatePicker } from "antd";
import { Link } from "react-router-dom";
import { DATE_PRESETS } from "../../../utils/date-presets";
import dayjs from "dayjs";
import { format, fromUnixTime } from "date-fns";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheckCircle, faWarning, faLock, faUnlock,
    faMoneyBillWave, faEllipsisVertical, faUserSecret, faSearch, faRotateLeft, faUserShield,
    faUserGroup, faEnvelope,
} from "@fortawesome/free-solid-svg-icons";
import authApi from "../../../api/authApi";
import { useAppSelector } from "../../../store";
import { canAccess } from "../../../config/admin-permissions";
import UserDeductModal from "./user-deduct-modal";
import UserSummaryBar from "./user-summary-bar";
import UserPermissionModal from "./user-permission-modal";
import UserEmailModal from "./user-email-modal";
import ReferrerEditModal from "../../../components/ReferrerEditModal";

const vnd = (v) => (v ? parseFloat(v).toLocaleString("vi-VN") : "—");

/** Nhãn + màu cho nguồn đăng ký (phương thức đăng nhập) */
const SRC_META = {
    google: { label: "Google", color: "red" },
    apple: { label: "Apple", color: "default" },
    email: { label: "Email", color: "blue" },
};
/** Nhãn + màu cho kênh đăng ký (app vs web) */
const CHANNEL_META = {
    app: { label: "App", color: "purple" },
    web: { label: "Web", color: "geekblue" },
};
const OS_LABEL = { ios: "iOS", android: "Android", web: "Web" };

const DEFAULT_FILTER = { limit: 20, page: 1, sort: "-userId", startDate: undefined, endDate: undefined };

/** Helper to build a repeated amount column definition */
const amtCol = (key, title) => ({
    title, dataIndex: key, key, align: "right",
    sorter: (a, b) => a[key] - b[key],
    render: vnd,
});

function User() {
    const [users, setUsers] = useState([]);
    const [totalUser, setTotalUser] = useState(0);
    const [userStats, setUserStats] = useState({ total: 0, active: 0 });
    const [isLoading, setIsLoading] = useState(false);
    const [filter, setFilter] = useState(DEFAULT_FILTER);
    // Ô nhập text là state nháp: chỉ áp dụng khi bấm "Tìm kiếm" / Enter (tránh tự
    // gọi API mỗi lần gõ phím -> gõ email dài là bắn cả chục request nửa vời).
    const [search, setSearch] = useState({ userId: "", name: "", email: "" });
    const [deductTarget, setDeductTarget] = useState(null);
    const [permTarget, setPermTarget] = useState(null);
    // Đổi email hộ user (user mất quyền truy cập email cũ) — chuyển từ php admin sang
    const [emailTarget, setEmailTarget] = useState(null);
    // Gán/sửa người giới thiệu — trang này là nơi DUY NHẤT tìm được user chưa có ref
    // (/referred-users chỉ liệt kê user đã có ref) nên là lối vào chính cho ca "quên nhập mã mời".
    const [refTarget, setRefTarget] = useState(null);
    // CTV (mod) chỉ thấy các thao tác mình được phép — thao tác admin-only ẩn đi cho
    // khỏi bấm rồi ăn 403 (khoá tk, trừ tiền, phân quyền).
    const userInfo = useAppSelector((state) => state.auth.userInfo);
    const isAdmin = userInfo?.role === "admin";
    const canEditReferrer = canAccess(userInfo, "referral");

    const fetchUsers = useCallback(async (f) => {
        setIsLoading(true);
        try {
            // Giữ cả giá trị 0 (Trạng thái "Bị khóa" = 0) — chỉ bỏ ô rỗng/chưa chọn.
            // encodeURIComponent cho tên/email vì có dấu cách và ký tự '+' trong email.
            // name/email dùng cú pháp [like] của APIFeature (Op.like) để tìm gần đúng —
            // CTV thường chỉ nhớ một phần tên/email. userId vẫn khớp chính xác.
            const query = Object.entries(f)
                .filter(([, v]) => v !== undefined && v !== null && v !== "")
                .reduce((acc, [k, v]) => {
                    const val = encodeURIComponent(v);
                    if (k === "startDate") acc.push(`createTime[gte]=${val}`);
                    else if (k === "endDate") acc.push(`createTime[lte]=${val}`);
                    else if (k === "name" || k === "email") acc.push(`${k}[like]=${encodeURIComponent(`%${v}%`)}`);
                    else acc.push(`${k}=${val}`);
                    return acc;
                }, [])
                .join("&");
            const { data } = await authApi.getAllUser(query);
            setUsers(data.users);
            setTotalUser(data.totalUser);
            if (data.stats) setUserStats(data.stats);
        } catch {
            message.error("Không thể tải danh sách người dùng");
        } finally {
            setIsLoading(false);
        }
    }, []);

    // Filter đã "chốt" (select/date/pagination/nút Tìm kiếm) → fetch ngay, không debounce
    useEffect(() => { fetchUsers(filter); }, [filter, fetchUsers]);

    const refresh = useCallback(() => fetchUsers(filter), [filter, fetchUsers]);
    const handleFilter = (key, value) => setFilter((p) => ({ ...p, [key]: value, page: 1 }));

    /** Áp dụng ô nhập text vào filter (bấm nút Tìm kiếm hoặc Enter). Nhận override để clear tức thì. */
    const applySearch = (override) => {
        const next = { ...search, ...override };
        setSearch(next);
        setFilter((p) => ({
            ...p,
            userId: next.userId.trim(),
            name: next.name.trim(),
            email: next.email.trim(),
            page: 1,
        }));
    };

    /** Gõ vào ô text: chỉ cập nhật nháp; nếu xoá trắng (nút clear) thì áp dụng luôn. */
    const onSearchChange = (key, value) => {
        if (!value) applySearch({ [key]: "" });
        else setSearch((p) => ({ ...p, [key]: value }));
    };

    /** Xoá bộ lọc: reset cả filter đã chốt lẫn ô nhập nháp. */
    const resetFilter = () => {
        setSearch({ userId: "", name: "", email: "" });
        setFilter(DEFAULT_FILTER);
    };

    /** Lock / Unlock confirmation via antd Modal.confirm */
    const showLockConfirm = (r) =>
        Modal.confirm({
            title: r.active ? "Khóa tài khoản" : "Mở khóa tài khoản",
            content: (
                <div>
                    <p className="font-bold">ID {r.id} – {r.name}</p>
                    <p className="text-content-3 text-sm">{r.email}</p>
                </div>
            ),
            okText: r.active ? "Khóa" : "Mở khóa",
            okType: r.active ? "danger" : "primary",
            cancelText: "Hủy",
            onOk: async () => {
                if (r.active) {
                    await authApi.lockAccount(r.id);
                    message.success("Đã khóa tài khoản");
                } else {
                    await authApi.unlockAccount(r.id);
                    message.success("Đã mở khóa tài khoản");
                }
                refresh();
            },
        });

    /** Login-as-user confirmation */
    const showLoginAsConfirm = (r) =>
        Modal.confirm({
            title: "Login as User",
            content: (
                <div>
                    <p className="font-bold">ID {r.id} – {r.name}</p>
                    <p className="text-sm text-orange-500 mt-1">Hành động này sẽ được ghi lại trong audit log</p>
                    {/* localStorage dùng chung theo origin: tab login-as sẽ thay phiên
                        longhousee.com hiện tại của chính bạn (đăng bài blog sẽ báo 403). */}
                    <p className="text-sm text-red-500 mt-1">
                        Lưu ý: tab mở ra sẽ thay phiên longhousee.com hiện tại của bạn. Bấm
                        "Thoát" ở thanh cam trên cùng để quay lại tài khoản của mình.
                    </p>
                </div>
            ),
            okText: "Xác nhận",
            cancelText: "Hủy",
            onOk: async () => {
                try {
                    const res = await authApi.loginAsUser(r.id);
                    const base = process.env.REACT_APP_FRONTEND_URL || window.location.origin.replace(/:\d+$/, ":3000");
                    window.open(`${base}/login-as?token=${encodeURIComponent(res.data.token)}&userId=${r.id}`, "_blank");
                    message.success(`Đăng nhập với tư cách user #${r.id}`);
                } catch (e) {
                    message.error(e?.response?.data?.message || "Không thể đăng nhập với tư cách user này");
                }
            },
        });

    const dataSource = users.map((el, i) => ({
        key: i,
        id: el.userId, email: el.email, name: el.name,
        pendingCash: el.pendingCash || 0, confirmCash: el.confirmCash || 0,
        withdraw: el.withdraw || 0, paid: el.paid || 0, bonus: el.bonus || 0,
        voucherSpent: el.voucherSpent || 0,
        pendingWithdraw: el.pendingWithdraw || 0,
        check: el.paid - el.withdraw - (el.pendingWithdraw || 0) + el.bonus - (el.voucherSpent || 0),
        cash: el.cash,
        active: el.active !== undefined ? el.active : true,
        createTime: el.createTime,
        registerSource: el.registerSource, // 'google' | 'apple' | 'email'
        registerChannel: el.registerChannel, // 'app' | 'web' | null
        registerOs: el.registerOs, // 'ios' | 'android' | 'web' | null
        ref: el.ref, // userId người giới thiệu
        createByIp: el.createByIp, // IP lúc đăng ký
        verified: el.verified,
        role: el.role, // 'user' | 'admin' | 'mod'
        permissions: el.permissions, // object quyền (mod)
    }));

    const columns = [
        {
            // Mở trang chi tiết user (số liệu + đơn / rút tiền / thưởng / giới thiệu)
            title: "ID", dataIndex: "id", key: "id", width: 62,
            render: (v) => <Link to={`/user/${v}`}>#{v}</Link>,
        },
        { title: "Tên", dataIndex: "name", key: "name", ellipsis: true },
        { title: "Email", dataIndex: "email", key: "email", ellipsis: true, width: 195 },
        {
            title: "Số dư", dataIndex: "cash", key: "cash", align: "right",
            sorter: (a, b) => a.cash - b.cash,
            // Số dư 0 là con số thật (vd. vừa rút hết) — hiện "0đ", không dùng "—" như các cột phụ.
            render: (v) => <span className="font-semibold text-green-700">{(parseFloat(v) || 0).toLocaleString("vi-VN")}đ</span>,
        },
        amtCol("pendingCash", "Chờ duyệt"),
        amtCol("confirmCash", "Chờ xác nhận"),
        amtCol("paid", "Đã duyệt"),
        amtCol("bonus", "Thưởng"),
        amtCol("withdraw", "Đã rút"),
        {
            title: "Đã đổi voucher", dataIndex: "voucherSpent", key: "voucherSpent", align: "right",
            sorter: (a, b) => a.voucherSpent - b.voucherSpent,
            render: (v) => <span className="text-purple-600">{vnd(v)}</span>,
        },
        {
            title: "Check", dataIndex: "check", key: "check", align: "center", width: 80,
            sorter: (a, b) => a.check - b.check,
            render: (v) => v >= 0
                ? <FontAwesomeIcon className="text-green-600" icon={faCheckCircle} />
                : <FontAwesomeIcon className="text-orange-600" icon={faWarning} />,
        },
        {
            title: "Trạng thái", dataIndex: "active", key: "active", width: 100,
            render: (active) => (
                <Tag color={active ? "green" : "red"}>{active ? "Hoạt động" : "Bị khóa"}</Tag>
            ),
        },
        {
            title: "Đăng ký từ", key: "registerSource", width: 150,
            render: (_, r) => {
                const src = SRC_META[r.registerSource] || { label: r.registerSource || "—", color: "default" };
                const ch = CHANNEL_META[r.registerChannel];
                return (
                    <div className="flex flex-wrap gap-1 items-center">
                        <Tag color={src.color} style={{ marginRight: 0 }}>{src.label}</Tag>
                        {ch && <Tag color={ch.color} style={{ marginRight: 0 }}>{ch.label}</Tag>}
                        {r.registerOs && (
                            <span className="text-xs text-content-3">{OS_LABEL[r.registerOs] || r.registerOs}</span>
                        )}
                    </div>
                );
            },
        },
        {
            title: "Giới thiệu", dataIndex: "ref", key: "ref", width: 90, align: "center",
            render: (v, r) => (
                <Button
                    type="link"
                    size="small"
                    className="px-0"
                    onClick={() => setRefTarget({ userId: r.id, name: r.name, email: r.email, ref: r.ref })}
                    title="Gán / sửa người giới thiệu"
                >
                    {v ? `#${v}` : "Gán"}
                </Button>
            ),
        },
        {
            title: "IP đăng ký", dataIndex: "createByIp", key: "createByIp", width: 130, ellipsis: true,
            render: (v) => v ? <span className="text-xs text-content-3">{v}</span> : "—",
        },
        {
            title: "Xác thực", dataIndex: "verified", key: "verified", width: 90, align: "center",
            render: (v) => <Tag color={v ? "green" : "default"}>{v ? "Đã xác thực" : "Chưa"}</Tag>,
        },
        {
            title: "Vai trò", dataIndex: "role", key: "role", width: 90, align: "center",
            render: (v) =>
                v === "admin" ? <Tag color="volcano">Admin</Tag>
                    : v === "mod" ? <Tag color="blue">CTV/Mod</Tag>
                        : <span className="text-content-3">—</span>,
        },
        {
            title: "Ngày đăng ký", dataIndex: "createTime", key: "createTime", width: 130,
            sorter: (a, b) => (a.createTime || 0) - (b.createTime || 0),
            // createTime stored as milliseconds (Date.now())
            render: (v) => v
                ? <span className="text-xs text-content-3 whitespace-nowrap">{format(fromUnixTime(v), "dd/MM/yyyy HH:mm")}</span>
                : "—",
        },
        {
            title: "", key: "actions", width: 48, fixed: "right",
            render: (_, r) => (
                <Dropdown
                    trigger={["click"]}
                    menu={{
                        items: [
                            isAdmin && {
                                key: "lock",
                                label: r.active ? "Khóa tài khoản" : "Mở khóa",
                                icon: <FontAwesomeIcon icon={r.active ? faLock : faUnlock} />,
                                onClick: () => showLockConfirm(r),
                            },
                            isAdmin && {
                                key: "deduct",
                                label: "Trừ tiền",
                                icon: <FontAwesomeIcon icon={faMoneyBillWave} />,
                                onClick: () => setDeductTarget(r),
                            },
                            // CTV không được vào tk admin/mod (backend cũng chặn) — tránh
                            // mượn token admin để tự nâng quyền.
                            (isAdmin || r.role === "user" || !r.role) && {
                                key: "loginAs",
                                label: "Login as User",
                                icon: <FontAwesomeIcon icon={faUserSecret} />,
                                onClick: () => showLoginAsConfirm(r),
                            },
                            canEditReferrer && {
                                key: "referrer",
                                label: r.ref ? "Sửa người giới thiệu" : "Gán người giới thiệu",
                                icon: <FontAwesomeIcon icon={faUserGroup} />,
                                onClick: () => setRefTarget({ userId: r.id, name: r.name, email: r.email, ref: r.ref }),
                            },
                            isAdmin && {
                                key: "email",
                                label: "Đổi email",
                                icon: <FontAwesomeIcon icon={faEnvelope} />,
                                onClick: () => setEmailTarget(r),
                            },
                            isAdmin && {
                                key: "permissions",
                                label: "Phân quyền (CTV)",
                                icon: <FontAwesomeIcon icon={faUserShield} />,
                                onClick: () => setPermTarget(r),
                            },
                        ].filter(Boolean),
                    }}
                >
                    <Button type="text" icon={<FontAwesomeIcon icon={faEllipsisVertical} />} />
                </Dropdown>
            ),
        },
    ];

    return (
        <div className="flex-1 flex flex-col p-5 overflow-auto gap-3">
            <h1 className="text-xl font-bold">Danh sách người dùng</h1>

            {/* Search / filter bar */}
            <div className="bg-surface border border-line rounded-lg p-3 flex gap-2 flex-wrap items-center shadow-sm">
                <Input
                    prefix={<FontAwesomeIcon icon={faSearch} className="text-content-3 mr-1" />}
                    placeholder="ID"
                    value={search.userId}
                    onChange={(e) => onSearchChange("userId", e.target.value)}
                    onPressEnter={() => applySearch()}
                    style={{ width: 110 }}
                    allowClear
                />
                <Input
                    prefix={<FontAwesomeIcon icon={faSearch} className="text-content-3 mr-1" />}
                    placeholder="Tên"
                    value={search.name}
                    onChange={(e) => onSearchChange("name", e.target.value)}
                    onPressEnter={() => applySearch()}
                    style={{ width: 160 }}
                    allowClear
                />
                <Input
                    prefix={<FontAwesomeIcon icon={faSearch} className="text-content-3 mr-1" />}
                    placeholder="Email"
                    value={search.email}
                    onChange={(e) => onSearchChange("email", e.target.value)}
                    onPressEnter={() => applySearch()}
                    style={{ width: 200 }}
                    allowClear
                />
                <Button
                    type="primary"
                    icon={<FontAwesomeIcon icon={faSearch} />}
                    loading={isLoading}
                    onClick={() => applySearch()}
                >
                    Tìm kiếm
                </Button>
                <Select
                    placeholder="Trạng thái"
                    value={filter.active ?? null}
                    onChange={(v) => handleFilter("active", v)}
                    allowClear
                    style={{ width: 140 }}
                    options={[
                        { value: 1, label: "Hoạt động" },
                        { value: 0, label: "Bị khóa" },
                    ]}
                />
                <Select
                    placeholder="Đăng ký từ"
                    value={filter.registerSource ?? null}
                    onChange={(v) => handleFilter("registerSource", v)}
                    allowClear
                    style={{ width: 140 }}
                    options={[
                        { value: "google", label: "Google" },
                        { value: "apple", label: "Apple" },
                        { value: "email", label: "Email" },
                    ]}
                />
                <Select
                    placeholder="Kênh"
                    value={filter.registerChannel ?? null}
                    onChange={(v) => handleFilter("registerChannel", v)}
                    allowClear
                    style={{ width: 120 }}
                    options={[
                        { value: "app", label: "App" },
                        { value: "web", label: "Web" },
                    ]}
                />
                <DatePicker.RangePicker
                    placeholder={["Ngày đăng ký từ", "đến"]}
                    format="DD/MM/YYYY"
                    allowClear
                    presets={DATE_PRESETS}
                    value={
                        filter.startDate && filter.endDate
                            ? [dayjs.unix(filter.startDate), dayjs.unix(filter.endDate)]
                            : undefined
                    }
                    onChange={(value) => {
                        if (!value) {
                            setFilter((p) => ({ ...p, startDate: undefined, endDate: undefined, page: 1 }));
                        } else {
                            setFilter((p) => ({
                                ...p,
                                startDate: value[0].startOf("day").unix(),
                                endDate: value[1].endOf("day").unix(),
                                page: 1,
                            }));
                        }
                    }}
                />
                <Button
                    icon={<FontAwesomeIcon icon={faRotateLeft} />}
                    onClick={resetFilter}
                    title="Xóa bộ lọc"
                />
            </div>

            <UserSummaryBar stats={userStats} />

            <Table
                size="small"
                dataSource={dataSource}
                columns={columns}
                pagination={false}
                loading={isLoading}
                scroll={{ x: "max-content" }}
                rowClassName={() => "whitespace-nowrap"}
            />

            <div className="flex justify-between items-center flex-wrap gap-2">
                <span className="text-sm text-content-3">Tổng: {totalUser.toLocaleString()} người dùng</span>
                <Pagination
                    total={totalUser}
                    current={filter.page}
                    pageSize={filter.limit}
                    showSizeChanger
                    pageSizeOptions={[20, 50, 100, 200]}
                    onChange={(page, limit) => setFilter((p) => ({ ...p, page, limit }))}
                />
            </div>

            <UserDeductModal
                user={deductTarget}
                onClose={() => setDeductTarget(null)}
                onSuccess={() => { setDeductTarget(null); refresh(); }}
            />

            <UserPermissionModal
                user={permTarget}
                onClose={() => setPermTarget(null)}
                onSuccess={() => { setPermTarget(null); refresh(); }}
            />

            <UserEmailModal
                user={emailTarget}
                onClose={() => setEmailTarget(null)}
                onSuccess={() => { setEmailTarget(null); refresh(); }}
            />

            <ReferrerEditModal
                target={refTarget}
                onClose={() => setRefTarget(null)}
                onSuccess={() => { setRefTarget(null); refresh(); }}
            />
        </div>
    );
}

export default User;
